import React, { Dispatch, FormEvent, SetStateAction, useEffect, useState, useCallback } from 'react';
import styles from './inviteModal.module.scss';
import Modal from '../../../../components/modal/Modal';
import { TextInput, TextInputValue } from '../../../../components/text-input/TextInput';
import Button from '../../../../components/button/Button';
import { isEmptyObject, validateEmail, validateIsEmpty } from '../../../../utils/validation';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { Dictionary, SelectOption, User, UserRoleEnum } from '../../../../types';
import { useQueryDictionary } from '../../../../hooks/useQueryDictionary';
import Select from '../../../../components/select/Select';
import Checkbox from '../../../../components/checkbox/Checkbox';
import { UserFragmentDoc, UserStatus } from '@api/graphql-codegen';
import DeleteIcon from '../../../../assets/icons/people_delete.svg?react';
import LocalConfirmation from '../../../../components/local-confirmation/LocalConfirmation';
import { useAnalytics } from '../../../../hooks/useAnalytics';
import { Events } from '../../../../services/Events';
import RadioGroup from "../../../../components/radio-group/RadioGroup";
import SelectMulti from '../../../../components/select-multi/SelectMulti';
import { useToast } from '@src/hooks';
import { logger } from '../../../../utils/logger';

type Props = {
  open: boolean;
  onClose: () => void;
  user?: User;
  me: User;
  handleSearch: () => void;
};

const emptyErrors = {
  firstName: '',
  lastName: '',
  email: '',
  account: '',
  school: '',
  adminUser: '',
  districtSchools: '',
};

export const options = [
  {label: "Teacher", value: UserRoleEnum.User},
  {label: "School Admin", value: UserRoleEnum.Admin},
  {label: "District Admin", value: UserRoleEnum.DistrictAdmin},
  {label: "OP Content Admin", value: UserRoleEnum.ContentAdmin},
  {label: "OP Super Admin", value: UserRoleEnum.SuperAdmin}
]

type ErrorFields = Partial<Record<keyof typeof emptyErrors, string>>;

const mutationCreateInvite = gql`
  mutation CreateInvite($input: SuperAdminInviteUserInput!) {
    superAdminInviteUser(input: $input) {
      ...User
    }
  }
  ${UserFragmentDoc}
`;

const mutationEditInvite = gql`
  mutation UpdateInvite($input: SuperAdminInviteUserInput!) {
    superAdminInviteUserEdit(input: $input) {
      ...User,
    }
  }
  ${UserFragmentDoc}
`;

const queryAccountSchools = gql`
  query Schools($input: UserIDInput!) {
    accountSchools(input: $input) {
      schools {
        name
        id
      }
    }
  }
`;

const queryAdminUsers = gql`
  query AdminUsers($input: UserIDInput!) {
    adminUsers(input: $input) {
      id
      name
    }
  }
`;

type MutationCreteInviteResponse = {
  superAdminInviteUser: User;
}

type MutationUpdateInviteResponse = {
  superAdminInviteUserEdit: User;
}

type QueryAccountSchoolsResponse = {
  accountSchools: {
    schools: Dictionary[];
    groups: Dictionary[];
    dataCollections: Dictionary[];
  }
}

type AdminUsersResponse = {
  adminUsers: Dictionary[];
}

const RemoveUserMutation = gql`
  mutation RemoveUser($input: UserIDInput!) {
    superAdminRemoveUser(input: $input)
  }
`;

type RemoveUserResponse = {
  superAdminRemoveUser: boolean;
};

function InviteModal(props: Props) {
  const {open, onClose, user, me, handleSearch} = props;

  const {trackEvent} = useAnalytics();
  const { successToast, errorToast } = useToast();

  const [getSchools, {loading: schoolOptionsLoading}] = useLazyQuery<QueryAccountSchoolsResponse>(queryAccountSchools, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      setSchoolOptions((res?.accountSchools?.schools ?? []).map(s => {
        return {
          value: s.id,
          label: s.name,
        };
      }));
    }
  });

  const [getAdminUsers, {loading: adminUsersLoading}] = useLazyQuery<AdminUsersResponse>(queryAdminUsers, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      setAdminUserOptions((res?.adminUsers ?? []).map(s => {
        return {
          value: s.id,
          label: s.name,
        };
      }));
    }
  });

  const [removeUser, {loading: removeLoading}] = useMutation<RemoveUserResponse>(RemoveUserMutation, {
    onError(err) {
      setBeError(err.message);
    },
    onCompleted(data) {
      if (data.superAdminRemoveUser) {
        trackEvent(Events.actions.userRemoved, Events.mappers.user(user));
        successToast("User successfully deleted!");
        clearFields();
        onClose();
        handleSearch();
      } else {
        errorToast('Remove failed, Please try again after refresh.')
      }
    },
    // update(cache, response, {variables}) {
    //   if (response.data?.superAdminRemoveUser && variables) {
    //     const usersResponse = cache.readQuery<UsersResponse>({query: usersQuery});
    //     cache.writeQuery({
    //       query: usersQuery,
    //       data: {
    //         users: usersResponse?.users.filter(u => u.id !== variables.userId) ?? [],
    //       }
    //     });
    //   }
    // }
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [disabled, setDisabled] = useState(false);
  const [error, setError] = useState<ErrorFields>(emptyErrors);
  const [beError, setBeError] = useState('');
  const [customer, setCustomer] = useState('');

  const [userType, setUserType] = useState('User');
  const [admin, setAdmin] = useState(false);
  const [districtAdmin, setDistAdmin] = useState(false);
  const [contentAdmin, setContentAdmin] = useState(false);
  const [superAdmin, setSuperAdmin] = useState(false);

  const [trial, setTrial] = useState(false);
  const [school, setSchool] = useState('');
  const [districtSchools, setDistrictSchools] = useState<Array<string>>([]);
  const [schoolOptions, setSchoolOptions] = useState<SelectOption[]>([]);
  const [adminUserOptions, setAdminUserOptions] = useState<SelectOption[]>([]);
  const [adminUser, setAdminUser] = useState('');
  const [removeConfirmation, setRemoveConfirmation] = useState(false);

  const accountOptions = useQueryDictionary({ dictionary: 'Customers' });

  const isEdit = !!user;

  useEffect(() => {
    setAdmin(userType === UserRoleEnum.Admin);
    setDistAdmin(userType === UserRoleEnum.DistrictAdmin);
    setContentAdmin(userType === UserRoleEnum.ContentAdmin);
    setSuperAdmin(userType === UserRoleEnum.SuperAdmin);
  }, [userType]);

  useEffect(() => {
    if (customer) {
      getSchools({
        variables: {
          input: { id: customer }
        }
      });
    }
  }, [getSchools, customer]);

  useEffect(() => {
    if (school && userType === 'User') {
      getAdminUsers({ variables: { input: { id: school } } });
    }
  }, [getAdminUsers, school, userType]);

  useEffect(() => {
    setFirstName(user?.firstName ?? '');
    setLastName(user?.lastName ?? '');
    setEmail(user?.email ?? '');
    setCustomer(user?.customer?.id ?? '');
    setTrial(user?.customer?.trial ?? false);
    setDisabled(user?.disabled || false);
    setSchool(user?.schools?.[0]?.id ?? '');
    setAdmin(user?.role === UserRoleEnum.Admin || false);
    setDistAdmin(user?.role === UserRoleEnum.DistrictAdmin || false);
    setContentAdmin(user?.role === UserRoleEnum.ContentAdmin || false);
    setSuperAdmin(user?.role === UserRoleEnum.SuperAdmin || false);
    const role = user?.role || UserRoleEnum.User;
    setUserType(role);
    setAdminUser(user?.admin ?? '');
    if (user?.districtSchools) {
      try {
        const districtSchools = JSON.parse(user?.districtSchools);
        if (districtSchools?.length) {
          setDistrictSchools(districtSchools);
        }
      } catch (e) {
        logger.info('parse error--', e);
      }
    }
  }, [user]);

  const [createInvite, {loading}] = useMutation<MutationCreteInviteResponse>(mutationCreateInvite, {
    onError(e) {
      setBeError(e.message);
    },
    update(cache, response) {
      // const u = response.data?.superAdminInviteUser;
      // if (u) {
      //   const usersResponse = cache.readQuery<UsersResponse>({query: usersQuery});
      //   cache.writeQuery({
      //     query: usersQuery,
      //     data: {
      //       users: [u, ...usersResponse?.users || []],
      //     }
      //   });
      // }

      clearFields();
      onClose();
      handleSearch();
    }
  });

  const [updateInvite, {loading: updateLoading}] = useMutation<MutationUpdateInviteResponse>(mutationEditInvite, {
    onError(e) {
      setBeError(e.message);
    },
    update(cache, response) {
      const u = response.data?.superAdminInviteUserEdit;
      if (u) {
        cache.writeFragment({
          id: `User:${u.id}`,
          data: {
            ...u,
          },
          fragment: UserFragmentDoc,
        });
      }
      clearFields();
      onClose();
      handleSearch();
    }
  });

  const handleChangeText = (setter: Dispatch<SetStateAction<string>>, key: keyof typeof emptyErrors) => (value: TextInputValue) => {
    setter(value as string);
    setError({
      ...error,
      [key]: '',
    });
  };

  const handleChangeOption = (setter: Dispatch<SetStateAction<string>>, key: keyof typeof emptyErrors) => (value) => {
    setter(value.value);
    setError({
      ...error,
      [key]: '',
    });

    if (key === 'account') {
      setTrial(value.trial)
      setSchool('');
      setSchoolOptions([]);
      setAdminUser('');
      setAdminUserOptions([]);
    }

    if (key === 'school') {
      setAdminUser('');
      setAdminUserOptions([]);
    }
  };

  const handleChangeMultiSelect = useCallback((options: string[]) => {
    setDistrictSchools(options);
  }, []);

  const clearFields = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setDisabled(false);
    setCustomer('');

    setAdminUser('');
    setAdmin(false);
    setDistAdmin(false);
    setContentAdmin(false);
    setSuperAdmin(false);

    setSchoolOptions([]);
    setAdminUserOptions([]);
    setBeError('');
    setError(emptyErrors);
  };

  const handleClose = () => {
    clearFields();
    onClose();
  };

  const validate = () => {
    const errors: ErrorFields = {};

    if (validateEmail(email)) {
      errors.email = validateEmail(email);
    }

    if (validateIsEmpty(firstName)) {
      errors.firstName = 'First name must not be empty.';
    }

    if (validateIsEmpty(lastName)) {
      errors.lastName = 'Last name must not be empty.';
    }

    if (validateIsEmpty(customer)) {
      errors.account = 'Account must not be empty.';
    }

    if (admin && validateIsEmpty(school)) {
      errors.school = 'School must not be empty for School Admin.';
    }

    if (!admin && !superAdmin && !districtAdmin) {
      if (validateIsEmpty(school)) {
        errors.school = 'Please select a school first.';
      }
    }

    return errors;
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const errors = validate();
    if (isEmptyObject(errors)) {
      setError(emptyErrors);
      setBeError('');
      if (isEdit) {
        updateInvite({
          variables: {
            input: {
              firstName,
              lastName,
              email,
              disabled,
              customer,
              school,
              admin,
              districtAdmin,
              superAdmin,
              contentAdmin,
              id: user!.id,
              adminId: adminUser,
              districtSchools: districtAdmin ? JSON.stringify(districtSchools) : undefined,
            }
          },
        });
      } else {
        createInvite({
          variables: {
            input: {
              firstName,
              lastName,
              email,
              disabled,
              customer,
              school,
              admin,
              districtAdmin,
              superAdmin,
              adminId: adminUser,
              districtSchools: districtAdmin ? JSON.stringify(districtSchools) : undefined
            }
          },
        });
      }
    } else {
      setError(errors);
    }
  };

  const handleUpdateInvite = () => {
    const errors = validate();

    if (isEmptyObject(errors)) {
      setError(emptyErrors);
      setBeError('');
      updateInvite({
        variables: {
          input: {
            firstName,
            lastName,
            email,
            disabled,
            customer,
            school,
            admin,
            districtAdmin,
            superAdmin,
            id: user!.id,
            resendInvite: true,
            adminId: adminUser,
            districtSchools: districtAdmin ? JSON.stringify(districtSchools) : undefined
          }
        },
      });
    } else {
      setError(errors);
    }
  }

  const handleRemove = () => {
    user && removeUser({
      variables: {
        input: {
          id: user.id,
        }
      },
    });
  };

  const handleConfirmRemove = () => {
    handleRemove();
    setRemoveConfirmation(false);
  };

  const handleCancel = () => {
    setRemoveConfirmation(false);
  };

  const handleShowConfirm = () => {
    setRemoveConfirmation(true);
  };

  const isLoading = loading || updateLoading || removeLoading;

  return (
    <Modal open={open} onClose={handleClose} className={styles.wrapper}>
      <div className={styles.title}>{isEdit ? 'Edit User Information' : 'Invite user'}</div>
      <div className={styles.deleteWrapper}>
        {isEdit && me.id !== user!.id &&
        <LocalConfirmation onConfirm={handleConfirmRemove} onCancel={handleCancel} open={removeConfirmation}>
         <Button disabled={isLoading} type={'button'} onClick={handleShowConfirm}
                 className={styles.removeBtn}
                 color={'red'}
                 size={'extraSmall'}
                 label={'Delete user'}
                 leftIcon={<DeleteIcon/>}/>
        </LocalConfirmation>
        }
      </div>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={styles.content}>
          <TextInput hasError={!!error.firstName} subTitle={error.firstName} placeholder={'First Name'}
                     inline={true}
                     label={'First name'} id={'firstName'} value={firstName}
                     onChange={handleChangeText(setFirstName, 'firstName')}/>
          <TextInput hasError={!!error.lastName} subTitle={error.lastName} placeholder={'Last Name'}
                     inline={true}
                     label={'Last name'} id={'lastName'} value={lastName}
                     onChange={handleChangeText(setLastName, 'lastName')}/>
          <TextInput allowSpecialCharacters hasError={!!error.email} subTitle={error.email} placeholder={'Email'} inline={true}
                     label={'Email'}
                     id={'email'} value={email}
                     onChange={handleChangeText(setEmail, 'email')}/>
          <Select error={!!error.account} subTitle={error.account} label={'Account'} options={accountOptions}
                  value={customer}
                  onChange={handleChangeOption(setCustomer, 'account')} inline={true} searchAutoFocus/>
          <Select error={!!error.school} subTitle={error.school}
                  disabled={schoolOptionsLoading || schoolOptions.length === 0} label={'School'}
                  options={schoolOptions}
                  value={school}
                  loading={schoolOptionsLoading}
                  onChange={handleChangeOption(setSchool, 'school')} inline={true} searchAutoFocus/>
          {districtAdmin ? (
            <SelectMulti label={'District schools'} options={schoolOptions} value={districtSchools}
                  onChange={handleChangeMultiSelect} showSearch searchAutoFocus
                  error={districtAdmin && !!error.districtSchools}
                  subTitle={error.districtSchools || undefined}
                  disabled={schoolOptionsLoading || schoolOptions.length === 0}
                  inline
            />
          ) : null}

          <div className={styles.inline}>
            <label className={styles.label}>User Type</label>
            <div>
              <div/>
              <RadioGroup
                value={userType}
                options={options}
                onChange={(val) => setUserType(val)}
                name={''}
                hasError={false}
                subTitle={""}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div/>
            <Checkbox disabled={true} id={'trial'} checked={trial} onChange={() => {}} label={'Trial'}/>
          </div>
          <div className={styles.row}>
            <div/>
            <Checkbox id={'disable'} checked={disabled} onChange={() => setDisabled(!disabled)} label={'Disable'}/>
          </div>
          {admin || districtAdmin || contentAdmin || superAdmin ? '' : (
            <Select error={!!error.adminUser} subTitle={error.adminUser}
                    label={'Admin User'} options={adminUserOptions} value={adminUser}
                    disabled={adminUsersLoading || !adminUserOptions.length}
                    loading={adminUsersLoading}
                    onChange={handleChangeOption(setAdminUser, 'adminUser')} inline={true} searchAutoFocus/>
          )}
          {Boolean(beError) && <div className={styles.error}>{beError}</div>}
          <div className={styles.action}>
            <div/>
            <Button disabled={isLoading || updateLoading} type={'submit'}
                    label={isEdit ? 'Save' : 'Send Invite'}/>
            {(isEdit && user.status !== UserStatus.ACCEPTED) && <>
             <div/>
             <Button className={styles.resendBtn}
                     disabled={isLoading || updateLoading}
                     type={'button'} label={'Save and Resend the Invite'}
                     onClick={handleUpdateInvite}
             />
            </>}
          </div>
        </div>
      </form>
    </Modal>
  );
}

export default InviteModal;
